import { useState, useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ReactSVG } from "react-svg";
import { FaRegTrashAlt, FaSpinner } from "react-icons/fa";
import { toast } from "react-toastify";
import api from "../../api/api";
import { IoIosArrowBack } from "react-icons/io";
import { useTranslation } from "react-i18next";
import { FaPencil } from "react-icons/fa6";
import { useProfileStore } from "../../store/useUserStore";
import { driver } from "driver.js";
import { motion, AnimatePresence } from "framer-motion";
import { TbBuildingBank } from "react-icons/tb";
import { formatTimeSlotList } from "../../helpers/helper";
import { FaHistory } from "react-icons/fa";

interface Month {
  id: string;
  name: string;
  date: Date;
}

const Cart = () => {
  // let cartTotalPrice = 0;
  const [cartItems, setCartItems] = useState([]);
  const [academyBreakdown, setAcademyBreakdown] = useState<any[]>([]);
  const [innerLoading, setInnerLoading] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [notificationCount, setNotificationCount] = useState(0);
  const {
    setHomePageShow,
    setSelectedStudent,
    setCartEdit,
    setLoading,
    selectedVouchers,
    setGuidanceStep,
    guidanceStep,
    showGuidanceFlow,
    setShowGuidanceFlow,
  } = useProfileStore();

  // Show driver.js popover for after-first-payment guidance
  useEffect(() => {
    if (guidanceStep === "after-first-payment") {
      const driverObj = driver({
        showProgress: false,
        showButtons: [],
        steps: [
          {
            element: "#payment-history-icon",
            popover: {
              description:
                "<img style='width: 170px; height: 166.65px;' src='/assets/img/5.png' />",
              side: "bottom",
              align: "center",
              onPopoverRender: (popover, { config }) => {
                const popoverElement = popover.wrapper;
                popoverElement.style.backgroundColor = "transparent";
                popoverElement.style.boxShadow = "none";
                const popoverDescription = popoverElement.querySelector(
                  "#driver-popover-description",
                ) as HTMLElement;
                if (popoverDescription) {
                  popoverDescription.style.textAlign = "center";
                }
              },
              showButtons: [],
            },
          },
        ],
        onCloseClick: () => {
          setGuidanceStep(null);
        },
        onPopoverRender: (popover, { config }) => {
          const popoverElement = popover.wrapper;
          const descriptionElement = popoverElement.querySelector(
            ".driver-popover-description",
          ) as HTMLElement;
          if (descriptionElement) {
            descriptionElement.style.textAlign = "center";
          }
        },
      });
      driverObj.drive();
    }
  }, [guidanceStep, setGuidanceStep]);

  // State to manage timeslots schedules modal
  const [openScheduleModal, setOpenScheduleModal] = useState(false);
  const [selectedScheduleList, setSelectedScheduleList] = useState([]);

  useEffect(() => {
    getCartItems();
    getOrderNotificationCount();
  }, []);

  function getOrderNotificationCount() {
    // get payment notification count
    api
      .get(`${process.env.PUBLIC_URL}/order/notification/summary`)
      .then((res) => {
        console.log("res notif", res);
        setNotificationCount(res.data.data.seen_count);
      })
      .catch((err) => {
        console.log("err", err);
      });
  }
  const [waivedSet, setWaivedSet] = useState<Set<string>>(new Set());

  function organizeCartData(data: any[]) {
    const organized: any[] = [];

    data.forEach((student) => {
      // Group cart items by branch
      const branchesMap: { [key: string]: any[] } = {};

      student.cart.forEach((item: any) => {
        const branchName = item.branch_name;

        if (!branchesMap[branchName]) {
          branchesMap[branchName] = [];
        }

        branchesMap[branchName].push(item);
      });

      // Convert branches map to array format
      const branches = Object.keys(branchesMap).map((branchName) => ({
        branch_name: branchName,
        items: branchesMap[branchName],
      }));

      // Add student with organized branches
      organized.push({
        student_id: student.student_id,
        name: student.student_name,
        branches: branches,
      });
    });

    console.log("organized cart data", organized);
    return organized;
  }

  // Usage in component:
  const [organizedCart, setOrganizedCart] = useState<any[]>([]);

  useEffect(() => {
    if (cartItems && cartItems.length > 0) {
      const organized = organizeCartData(cartItems);
      setOrganizedCart(organized);
    }
  }, [cartItems]);

  function getCartItems() {
    setLoading(true);
    api
      .get(`${process.env.REACT_APP_API_BASE_URL}/cart`)
      .then((res) => {
        console.log("res cart", res.data.data);
        setCartItems(res.data.data.students);

        // Filter out academy breakdown items with all zero discount values
        const academyBreakdownData =
          res.data.data?.discount_summary?.academy_breakdown;

        // Handle case where academy_breakdown might be an empty object {} instead of array
        let filteredAcademyBreakdown: any[] = [];
        if (Array.isArray(academyBreakdownData)) {
          filteredAcademyBreakdown = academyBreakdownData.filter(
            (academy: any) => {
              const waveDiscount = parseFloat(academy.wave_discount_total || 0);
              const bundleDiscount = parseFloat(
                academy.bundle_discount_total || 0,
              );
              const siblingDiscount = parseFloat(
                academy.sibling_discount_total || 0,
              );
              const batchDiscount = parseFloat(
                academy.batch_discount_total || 0,
              );

              // Keep academy if any discount is greater than 0
              return (
                bundleDiscount > 0 ||
                siblingDiscount > 0 ||
                batchDiscount > 0 ||
                waveDiscount > 0
              );
            },
          );
        }

        setAcademyBreakdown(filteredAcademyBreakdown);

        const newWaivedSet = new Set<string>();
        res.data.data.students.forEach((student) => {
          student.cart.forEach((item) => {
            if (item.type === "wave") {
              if (item.payment_method === "month") {
                const key = `${item.course_branch_id}_${item.selected_date}`;
                newWaivedSet.add(key);
              } else if (item.payment_method == "attandance") {
                const key = `${item.course_branch_id}_${item.selected_package.id}`;
                newWaivedSet.add(key);
              } else if (item.payment_method == "attandance-no replace") {
                const key = `${item.course_branch_id}_${item.selected_package.id}`;
                newWaivedSet.add(key);
              }
            }
          });
        });
        setWaivedSet(newWaivedSet);
      })
      .catch((err) => {
        toast.error(t("server_error"));
        console.log("err cart", err);
      })
      .finally(() => {
        setLoading(false);
      });
  }

  function deleteCartItem(cartItem: any) {
    console.log("deleteCartItem");
    setInnerLoading(true);
    api
      .delete(`${process.env.PUBLIC_URL}/cart/delete/${cartItem.id}`)
      .then((res) => {
        setInnerLoading(false);
        toast.success("Delete from cart");
        console.log("res", res);
        getCartItems();
      })
      .catch((err) => {
        setInnerLoading(false);
        toast.error(t("server_error"));
        console.log("err", err);
      });
  }

  function handleCheckout() {
    console.log("handleCheckout");
    setLoading(true);
    api
      .post(`${process.env.PUBLIC_URL}/cart/checkout`, {
        final_price: cartTotalPrice.toFixed(2),
      })
      .then((res) => {
        console.log("res", res);

        if (res.data.skip_payment === true) {
          toast.success(t("cart.payment_success"));
          getCartItems();
          return;
        }

        const paymentUrl = res.data.payment_url;
        if (paymentUrl) {
          window.location.href = paymentUrl;
        } else {
          toast.error(t("url_not_found"));
        }
      })
      .catch((error) => {
        // Show API error message if available
        if (
          error.response &&
          error.response.data &&
          error.response.data.errors
        ) {
          const errors = error.response.data.errors;

          // Handle non_field_errors
          if (
            errors.non_field_errors &&
            Array.isArray(errors.non_field_errors)
          ) {
            errors.non_field_errors.forEach((errorMessage: string) => {
              toast.error(errorMessage);
            });
          } else {
            // Handle other field-specific errors
            Object.keys(errors).forEach((field) => {
              if (Array.isArray(errors[field])) {
                errors[field].forEach((errorMessage: string) => {
                  toast.error(`${field}: ${errorMessage}`);
                });
              }
            });
          }
        } else if (
          error.response &&
          error.response.data &&
          error.response.data.message
        ) {
          toast.error(error.response.data.message);
        } else if (error.response?.data?.error) {
          toast.error(error.response.data.error);
        } else {
          // Fallback error message
          toast.error("An unexpected error occurred.");
        }
        console.log("err", error);
      })
      .finally(() => {
        setLoading(false);
        // Check if cart is empty after payment (in case of no redirect)
        if (cartItems.length === 0) {
          setGuidanceStep("after-first-payment");
        }
      });
  }

  const [showMonthModal, setShowMonthModal] = useState(false);
  const [selectedCartItem, setSelectedCartItem] = useState<any>(null);
  const [selectedMonths, setSelectedMonths] = useState<string[]>([]);

  const getMonths = (): Month[] => {
    const months: Month[] = [];

    const startDate = new Date();
    startDate.setDate(1); // Start from the first day of the current month
    startDate.setMonth(startDate.getMonth() - 6); // Move 6 months back

    for (let i = 0; i < 24; i++) {
      // Total 24 months (6 past + current + 17 future)
      const month = new Date(startDate);
      month.setMonth(startDate.getMonth() + i);

      const year = month.getFullYear();
      const monthNumber = String(month.getMonth() + 1).padStart(2, "0");

      months.push({
        id: `${year}-${monthNumber}`,
        name: month.toLocaleString("default", {
          month: "short",
          year: "numeric",
        }),
        date: month,
      });
    }

    return months;
  };

  const months: Month[] = getMonths();

  const handleSelectMonth = (month: Month) => {
    setSelectedMonths((prev: any) =>
      prev.includes(month.id)
        ? prev.filter((id: any) => id !== month.id)
        : [...prev, month.id],
    );
  };
  const isChecked = (month: Month) => {
    return selectedMonths ? selectedMonths.includes(month.id) : false;
  };

  useEffect(() => {
    // Parse "Oct 2024" into a Date object (if selected_date exists)
    if (selectedCartItem?.selected_date) {
      const parsed = new Date(`${selectedCartItem.selected_date} 01`);
      const year = parsed.getFullYear();
      const month = String(parsed.getMonth() + 1).padStart(2, "0");
      const selectedId = `${year}-${month}`;

      setSelectedMonths((prev: string[]) => {
        if (!prev.includes(selectedId)) {
          return [...prev, selectedId];
        }
        return prev;
      });
    }
  }, [selectedCartItem]);

  function navigateCourseView(
    student_id: number,
    student_name: string,
    course_branch_id: number,
  ) {
    setHomePageShow("school");
    setSelectedStudent({ id: student_id, name: student_name });
    // setLastVisitedCourseId(course_branch_id.toString())
    return navigate(`/course/${course_branch_id}?from=cart`);
  }

  function navigateItemView(
    student_id: number,
    student_name: string,
    item_id: number,
    course_branch_id: number,
  ) {
    setHomePageShow("school");
    setSelectedStudent({ id: student_id, name: student_name });
    return navigate(
      `/item/${item_id}?course_branch_id=${course_branch_id}&from=cart`,
    );
  }

  const cartSubtotal = useMemo(() => {
    const courseGroups = new Map<
      string,
      {
        total: number;
        remaining: number;
        course_branch_id: number;
        selected_package_id?: number;
        selected_date?: string;
      }
    >();

    // 1. Group all course items
    cartItems.forEach((student: any) => {
      student.cart.forEach((item: any) => {
        if (item.type === "course") {
          const isMonth = item.pricing_method?.toLowerCase() === "month";
          const key = isMonth
            ? `${item.course_branch_id}`
            : `${item.course_branch_id}_${item.selected_package?.id}`;

          const itemPrice = parseFloat(item.price) || 0;
          if (!courseGroups.has(key)) {
            courseGroups.set(key, {
              total: itemPrice,
              remaining: itemPrice,
              course_branch_id: item.course_branch_id,
              selected_package_id: item.selected_package?.id,
              selected_date: item.selected_date,
            });
          } else {
            const existing = courseGroups.get(key)!;
            existing.total += itemPrice;
            existing.remaining += itemPrice;
          }
        }
      });
    });

    // 2. Deduct wave items
    cartItems.forEach((student: any) => {
      student.cart.forEach((item: any) => {
        if (item.type === "wave") {
          const isMonth = item.payment_method === "month";
          const key = isMonth
            ? `${item.course_branch_id}`
            : `${item.course_branch_id}_${item.selected_package?.id}`;

          const group = courseGroups.get(key);
          if (group) {
            const itemPrice = parseFloat(item.price) || 0;
            const deduction = Math.min(itemPrice, group.remaining);
            group.remaining -= deduction;
          }
        }
      });
    });

    // 3. Calculate subtotal (before academy discounts)
    let subtotal = 0;
    Array.from(courseGroups.values()).forEach((group) => {
      subtotal += group.remaining;
    });
    // 4. Add item type "item" price (not waived)
    cartItems.forEach((student: any) => {
      student.cart.forEach((item: any) => {
        if (item.type === "item") {
          const itemPrice = parseFloat(item.item?.price) || 0;
          const quantity = parseInt(item.quantity) || 1;
          subtotal += itemPrice * quantity;
        }
      });
    });

    console.log("cart subtotal", subtotal);

    return subtotal;
  }, [cartItems]);

  const cartTotalPrice = useMemo(() => {
    const academyDiscountTotal = academyBreakdown.reduce((total, academy) => {
      const bundleDiscount = parseFloat(academy.bundle_discount_total || 0);
      const siblingDiscount = parseFloat(academy.sibling_discount_total || 0);
      const batchDiscount = parseFloat(academy.batch_discount_total || 0);

      return total + bundleDiscount + siblingDiscount + batchDiscount;
    }, 0);

    console.log("academyDiscountTotal,", academyDiscountTotal);

    return Math.max(cartSubtotal - academyDiscountTotal, 0);
  }, [cartSubtotal, academyBreakdown]);

  return (
    <div className="pb-20">
      <div className="mb-2 flex items-center justify-between pl-2 pr-3">
        <IoIosArrowBack size={28} color="#000" onClick={() => navigate("/")} />
        <div className="text-lg font-medium flex-1 text-center">
          {t("cart.cart")}
        </div>
        <div className="relative">
          <FaHistory
            id="payment-history-icon"
            onClick={() => navigate("/payment-history")}
            className="ml-auto cursor-pointer w-6 h-6"
          />

          {/* Red Notification Badge */}
          {notificationCount > 0 && (
            <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-red-500 rounded-full"></span>
          )}
        </div>
      </div>

      <div className="flex">
        {/* My Active Course Button */}
        <Link to="/active-course" className="w-full mb-4 mt-8 px-3">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4 flex items-center justify-between hover:shadow-md transition-shadow">
            <span className="text-lg font-semibold text-gray-900">
              My Course
            </span>
            <svg
              className="w-5 h-5 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </div>
        </Link>
        <Link to="/pre-enroll" className="w-full mb-4 mt-8 px-3">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4 flex items-center justify-between hover:shadow-md transition-shadow">
            <span className="text-lg font-semibold text-gray-900">
              {t("course_view.pre_enrol")}
            </span>
            <svg
              className="w-5 h-5 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </div>
        </Link>
      </div>

      <div className="px-3">
        {cartItems && cartItems.length >= 1 ? (
          <>
            {organizedCart.map((student, studentIndex) => (
              <div key={studentIndex} className="border-gray-300 pb-8">
                <div className="text-lg font-semibold mt-3">{student.name}</div>
                {student.branches.map((branch: any, branchIndex: number) => (
                  <div key={branchIndex}>
                    <div className="flex items-center bg-blue-100 rounded-lg px-3 py-1 my-3">
                      <TbBuildingBank className="mr-2" />
                      <span className="font-medium text-sm text-gray-700">
                        {branch.branch_name}
                      </span>
                    </div>
                    {branch.items.map((item: any, index: number) => {
                      if (item.type === "course") {
                        return (
                          <>
                            <div
                              className={`grid grid-cols-5 gap-3 mt-5 ${item.validity.is_valid? "": "bg-gray-300"}`}
                              key={index}
                            >
                              <div className="col-span-1 self-center relative">
                                {/* Badge */}
                                {item.applied_discount_tags &&
                                  item.applied_discount_tags.length > 0 && (
                                    <div className="absolute top-0 left-0 z-10 flex flex-col gap-1">
                                      {item.applied_discount_tags.map(
                                        (tag: any, tagIndex: number) => (
                                          <div
                                            key={tagIndex}
                                            className={`text-white px-2 py-1 text-xs font-semibold rounded ${
                                              tag.type === "bundle"
                                                ? "bg-orange-400"
                                                : tag.type === "sibling"
                                                ? "bg-red-500"
                                                : ""
                                            }`}
                                          >
                                            {tag.type === "bundle"
                                              ? t("promotion.bundle")
                                              : tag.type === "sibling"
                                              ? t("promotion.sibling")
                                              : ""}
                                          </div>
                                        ),
                                      )}
                                    </div>
                                  )}

                                {/* Image */}
                                {item.course.images &&
                                item.course.images.length > 0 ? (
                                  <img
                                    src={item.course.images[0]}
                                    className="w-full aspect-square object-cover"
                                  />
                                ) : (
                                  <img
                                    src={"/assets/img/products/product1.png"}
                                    className="w-full aspect-square object-cover"
                                  />
                                )}
                              </div>
                              <div className="col-span-4">
                                <div className="grid grid-cols-3 gap-1">
                                  <div className="col-span-2">
                                    <div
                                      onClick={() =>
                                        navigateCourseView(
                                          student.student_id,
                                          student.name,
                                          item.course_branch_id,
                                        )
                                      }
                                      className={`text-lg font-medium truncate ${item.validity.is_valid ? "": "opacity-25"}`}
                                    >
                                      {item.course.name}
                                    </div>
                                    {item.validity.is_valid ? (
                                      <div
                                        className="truncate"
                                        onClick={() => {
                                          setSelectedScheduleList(item.schedules);
                                          setOpenScheduleModal(true);
                                        }}
                                      >
                                        {formatTimeSlotList(item.schedules)}
                                      </div>
                                    ): <div className="h-5"></div>}
                                  </div>
                                  

                                  <div className="flex flex-col col-span-1">
                                    {item.validity.is_valid ? (
                                      <div className="self-end">
                                        <div
                                          className={`rounded-xl text-center p-[0.12rem] px-2 text-xs truncate ${
                                            item.status === "added"
                                              ? "bg-green-500 text-white"
                                              : item.status === "overdue"
                                              ? "bg-red-500 text-white"
                                              : item.status === "pre_enroll"
                                              ? "text-white bg-[#023f81]"
                                              : "bg-amber-600 text-white"
                                          }`}
                                        >
                                          {item.status === "added"
                                            ? t("cart.newly_added")
                                            : item.status === "overdue"
                                            ? t("cart.overdue")
                                            : item.status === "pre_enroll"
                                            ? t("course_view.pre_enrol")
                                            : t("cart.due_soon")}
                                        </div>
                                      </div>
                                    ): <div className="h-5"></div>}
                                    <div className="self-end mt-2">
                                      {item.status !== "overdue" &&
                                        (innerLoading ? (
                                          <FaSpinner className="animate-spin" />
                                        ) : (
                                          <FaRegTrashAlt
                                            size={16}
                                            className="mt-1 cursor-pointer"
                                            onClick={() => deleteCartItem(item)}
                                          />
                                        ))}
                                    </div>
                                  </div>
                                  
                                </div>
                                
                                <div className="grid grid-cols-8 mt-2">
                                  {item.validity.is_valid ? (
                                    <div className=" whitespace-normal break-words col-span-5 flex justify-between items-center">
                                      {item.pricing_method === "Month"
                                        ? item.selected_date
                                        : item.selected_package.count + " attd"}
                                      {(item.status !== "overdue" && item.validity.is_valid)  && (
                                        <FaPencil
                                          className="cursor-pointer"
                                          onClick={() => {
                                            setCartEdit({
                                              ...item,
                                              student_id: student.student_id,
                                              student_name: student.name,
                                            });
                                            return navigate(
                                              `/course/${item.course_branch_id}/edit`,
                                            );
                                          }}
                                        />
                                      )}
                                    </div>
                                  ): <div className="col-span-5"></div>}
                                  <div className=" col-span-3 text-end font-medium">{`RM ${item.price}`}</div>
                                </div>
                              </div>
                            </div>
                          </>
                        );
                      } else if (item.type === "wave") {
                        let waiveBalancePrice = 0;

                        if (item.payment_method === "month") {
                          // cartTotalPrice -= (cartItem.price || 0);
                          waiveBalancePrice = item.price;
                        } else {
                          // cartTotalPrice -= (cartItem.selected_package?.price || 0);
                          waiveBalancePrice = item.price || 0;
                        }

                        return (
                          <>
                            {/* {cartItem.branch_name && shownBranches.add(cartItem.branch_name)} */}
                            <div
                              className="grid grid-cols-5 gap-3 mt-5"
                              key={index}
                            >
                              <div className="col-span-1 self-center">
                                {item.course.images &&
                                item.course.images.length > 0 ? (
                                  <img
                                    src={item.course.images[0]}
                                    className="w-full aspect-square object-cover"
                                  />
                                ) : (
                                  <img
                                    src={"/assets/img/products/product1.png"}
                                    className="w-full aspect-square object-cover"
                                  />
                                )}
                              </div>
                              <div className="col-span-4">
                                <div className="grid grid-cols-3 gap-1">
                                  <div className="col-span-2">
                                    <div
                                      onClick={() =>
                                        navigateCourseView(
                                          student.student_id,
                                          student.student_name,
                                          item.course_branch_id,
                                        )
                                      }
                                      className="text-lg font-medium truncate"
                                    >
                                      Waive Payment
                                    </div>
                                    <div>{item.course.name}</div>
                                  </div>
                                </div>

                                <div className="grid grid-cols-8 mt-2">
                                  <div className=" whitespace-normal break-words col-span-5 flex justify-between items-center">
                                    {item.selected_date}
                                  </div>
                                  <div className=" col-span-3 text-end text-red-600 font-medium">{`RM ${
                                    waiveBalancePrice || 0
                                  }`}</div>
                                </div>
                              </div>

                              <div className="col-span-5 p-2 border border-1 border-gray-300 rounded">
                                <span className="text-theme">
                                  {item.remark}
                                </span>
                              </div>
                            </div>
                          </>
                        );
                      } else if (item.type == "item") {
                        // cartTotalPrice += cartItem.item.price;
                        return (
                          <>
                            <div
                              className="grid grid-cols-5 gap-3 mt-5"
                              key={index}
                            >
                              <div className="col-span-1 self-center">
                                {item.item.images &&
                                item.item.images.length > 0 ? (
                                  <img
                                    src={item.item.images[0]}
                                    className="w-full aspect-square object-cover"
                                  />
                                ) : (
                                  <img
                                    src={"/assets/img/products/product1.png"}
                                    className="w-full aspect-square object-cover"
                                  />
                                )}
                              </div>
                              <div className="col-span-4">
                                <div className="grid grid-cols-3 gap-1">
                                  <div className="col-span-2 ">
                                    <div
                                      className="text-lg font-medium truncate"
                                      onClick={() =>
                                        navigateItemView(
                                          student.student_id,
                                          student.name,
                                          item.item.id,
                                          item.item.course_branch_id,
                                        )
                                      }
                                    >
                                      {item.item.name}
                                    </div>
                                  </div>
                                  <div className="col-span-1">
                                    <div className="justify-items-end">
                                      {item.deletable &&
                                        (innerLoading ? (
                                          <FaSpinner className="animate-spin" />
                                        ) : (
                                          <FaRegTrashAlt
                                            size={18}
                                            className="justify-self-end mt-1"
                                            onClick={() => {
                                              deleteCartItem(item);
                                            }}
                                          />
                                        ))}
                                    </div>
                                  </div>
                                </div>

                                <div className="mt-2">
                                  <div className="whitespace-normal break-words">
                                    {t("cart.quantity")}: {item.quantity}
                                  </div>
                                  <div className="text-end text-lg font-medium">{`RM ${
                                    item.item.price * (item.quantity || 1)
                                  }`}</div>
                                </div>
                              </div>
                            </div>
                          </>
                        );
                      }
                    })}
                  </div>
                ))}
              </div>
            ))}

            <div className="pb-8 pt-4 border-t border-gray-300">
              <div className="max-w-screen-md mx-auto">
                {/* Voucher/Discount Section */}

                {/* Subtotal */}
                <div className="flex justify-between items-center mb-3">
                  <span className="text-base">{t("cart.sub_total")}</span>
                  <span className="text-base">
                    RM {cartSubtotal.toFixed(2)}
                  </span>
                </div>

                {/* Academy Breakdown */}
                {academyBreakdown && academyBreakdown.length > 0 && (
                  <div className="border border-gray-200 rounded-lg p-4 mb-4">
                    {academyBreakdown.map((academy, index) => (
                      <div key={academy.academy_id}>
                        {/* Academy Name */}
                        <div className="font-medium text-gray-800 mb-2">
                          {academy.academy_name}
                        </div>

                        {/* Sibling Discount */}
                        {parseFloat(academy.sibling_discount_total) > 0 && (
                          <div className="flex justify-between items-center text-sm mb-1">
                            <span className="text-red-500">
                              {t("promotion.sibling_discount")}
                            </span>
                            <span className="text-red-500">
                              - RM{" "}
                              {parseFloat(
                                academy.sibling_discount_total,
                              ).toFixed(2)}
                            </span>
                          </div>
                        )}

                        {/* Bundle Discount */}
                        {parseFloat(academy.bundle_discount_total) > 0 && (
                          <div className="flex justify-between items-center text-sm mb-1">
                            <span className="text-orange-500">
                              {t("promotion.bundle_discount")}
                            </span>
                            <span className="text-orange-500">
                              - RM{" "}
                              {parseFloat(
                                academy.bundle_discount_total,
                              ).toFixed(2)}
                            </span>
                          </div>
                        )}

                        {parseFloat(academy.batch_discount_total) > 0 && (
                          <div className="flex justify-between items-center text-sm mb-1">
                            <span className="text-blue-500">
                              {t("promotion.batch_discount")}
                            </span>
                            <span className="text-blue-500">
                              - RM{" "}
                              {parseFloat(academy.batch_discount_total).toFixed(
                                2,
                              )}
                            </span>
                          </div>
                        )}

                        {/* Add spacing between academies if not the last one */}
                        {index < academyBreakdown.length - 1 && (
                          <div className="my-3"></div>
                        )}
                      </div>
                    ))}

                    {/* Total Saved */}
                    <div className="border-t border-gray-300 pt-3 mt-3">
                      <div className="flex justify-between items-center font-medium">
                        <span className="text-gray-700">
                          {t("cart.total_saved")}
                        </span>
                        <span className="text-gray-700">
                          - RM{" "}
                          {academyBreakdown
                            .reduce((total, academy) => {
                              const bundleDiscount = parseFloat(
                                academy.bundle_discount_total || 0,
                              );
                              const siblingDiscount = parseFloat(
                                academy.sibling_discount_total || 0,
                              );
                              const batchDiscount = parseFloat(
                                academy.batch_discount_total || 0,
                              );
                              return (
                                total +
                                bundleDiscount +
                                siblingDiscount +
                                batchDiscount
                              );
                            }, 0)
                            .toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Divider */}
                <div className="border-t border-gray-200 pt-4">
                  {/* Grand Total */}
                  <div className="flex justify-between items-center mb-6">
                    <span className="text-lg font-semibold">
                      {t("cart.grand_total")}
                    </span>
                    <span className="text-lg font-semibold">
                      RM {cartTotalPrice.toFixed(2)}
                    </span>
                  </div>
                </div>

                <button
                  onClick={handleCheckout}
                  type="button"
                  className="text-white text-[16px] leading-none w-full py-[15px] bg-theme border-none rounded-3xl flex items-center justify-center"
                >
                  {innerLoading ? (
                    <FaSpinner className="animate-spin" />
                  ) : (
                    t("cart.proceed_to_checkout")
                  )}
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className="flex flex-col items-center justify-center text-center mt-52">
            <div className="flex items-center justify-center w-[100px] h-[70px] mb-5">
              <ReactSVG
                src={process.env.PUBLIC_URL + "/assets/img/icons/cart.svg"}
              />
            </div>
            <div>
              <p>
                {t("cart.no_items_in_cart")}{" "}
                <Link
                  to={process.env.PUBLIC_URL + "/"}
                  className="block underline font-medium"
                >
                  {t("cart.add_some")}
                </Link>
              </p>
            </div>
          </div>
        )}
      </div>

      {showMonthModal && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white px-6 py-2 rounded-lg w-5/6 max-w-md min-h-[300px] flex flex-col justify-between">
            <div>
              <div className="text-lg font-semibold mb-4">
                {t("cart.edit_payment_term")}
              </div>
              <div
                className="mt-2 bg-white rounded-md w-full z-10"
                style={{ maxHeight: "13rem", overflowY: "auto" }}
              >
                {months.map((month) => (
                  <div
                    key={month.id}
                    className={`flex items-center px-1 py-1 hover:bg-gray-100 cursor-pointer`}
                    onClick={() => {
                      handleSelectMonth(month);
                    }}
                  >
                    <input
                      type="checkbox"
                      className="mr-2"
                      checked={isChecked(month)}
                      readOnly
                    />
                    <span className="text-lg">{month.name}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowMonthModal(false);
                  setSelectedMonths([]);
                }}
                className="px-4 py-1 text-gray-700 rounded-full hover:bg-gray-100 transition"
              >
                {t("close")}
              </button>
              <button
                className="px-4 py-1 bg-theme text-white rounded-full transition"
                onClick={() => {
                  setShowMonthModal(false);
                  setSelectedMonths([]);
                }}
              >
                {t("save")}
              </button>
            </div>
          </div>
        </div>
      )}

      <AnimatePresence>
        {openScheduleModal && (
          <div
            className="fixed inset-0 z-10 bg-black bg-opacity-50 flex justify-center items-end"
            onClick={() => {
              setOpenScheduleModal(false);
              setSelectedScheduleList([]);
            }}
          >
            <motion.div
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ stiffness: 100 }}
              className="w-full max-w-[648px] bg-white shadow-2xl rounded-t-3xl px-4 pt-4 pb-20"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="mb-10">
                {selectedScheduleList.map((slot: any, index) => {
                  const format12 = (time: string) => {
                    const [h, m] = time.split(":").map(Number);
                    const d = new Date();
                    d.setHours(h, m);
                    return d.toLocaleTimeString("en-US", {
                      hour: "2-digit",
                      minute: "2-digit",
                      hour12: true,
                    });
                  };

                  return (
                    <div key={index}>
                      {format12(slot?.start_time)} - {format12(slot?.end_time)}{" "}
                      {slot.day}
                    </div>
                  );
                })}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Cart;
